import { MessageType } from '@graphql/generated-base';
import type { InternalMessage } from './MessagesV2.types';

/**
 * Derives the "Status Message" shown per row from the message's status history.
 * Picks the most recent history entry matching the message's current rawStatus.
 *
 * `rawStatusHistory[].date` is the GraphQL `Date` scalar (an object exposing
 * `.milliseconds`), not a string — sort on `.milliseconds` directly.
 *
 * Single source of truth shared by the messages table and the CSV export so the
 * two can never diverge.
 */
export function deriveStatusMessage(m: InternalMessage): string {
  return (m.rawStatusHistory ?? []).filter(h => h.rawStatus === m.rawStatus).sort((a, b) => b.date.milliseconds - a.date.milliseconds)[0]?.statusMessage ?? '';
}

/** The provider every email is sent through — the one value the column has no reason to print. */
const IMPLIED_EMAIL_PROVIDER = 'mailgun';

/**
 * The provider label a row shows: the sending provider, blank only where it carries no information.
 *
 * `provider` is non-null on both channels, but an email row reads `mailgun` every time — a constant
 * the Channel column already implies, not an observation. Printing it down every row is noise in a
 * column whose job is telling twilio from telnyx at a glance, so that one combination renders the
 * same em-dash the table gives any absent value. Nothing is lost: `channel` sits beside it in both
 * the table and the export, so a blank reads back as `mailgun` exactly.
 *
 * Blanking is keyed to the exact email/`mailgun` pair rather than to the channel alone, so it stays
 * quiet about the expected case without going quiet about an unexpected one. An email sent through
 * some other provider is precisely what CX would need this column to show, and the "email is always
 * mailgun" rule is a backend convention nothing here can enforce.
 *
 * KNOWN LIMITATION: `InternalMessage` carries no channel finer than email/sms, so a WhatsApp send
 * reports `twilio` and is indistinguishable here from SMS-over-Twilio.
 *
 * Structurally typed rather than taking `InternalMessage`, so the expand-to-original subrow can
 * apply the identical rule to the narrower row `internalMessageById` returns. Both rows type
 * `type` as `MessageType`, so the narrower shape costs no type safety.
 *
 * Single source of truth shared by the messages table, the subrow and the CSV export so the three
 * can never diverge.
 */
export function deriveProviderLabel(m: Readonly<{ type: MessageType; provider: string }>): string {
  return m.type === MessageType.email && m.provider === IMPLIED_EMAIL_PROVIDER ? '' : m.provider;
}

/**
 * The group a row belongs to: a first send plus every resend of it.
 *
 * Every attempt points at the FIRST send via `originalMessageId`, never at the attempt it was made
 * from, so the group is a star and not a chain — three resends of one message share one key, they
 * do not form a sequence. `EmailService#resendWithNewEmailV2` is what guarantees it, by resolving
 * back to the root before it writes the link.
 */
const attemptGroupKey = (m: InternalMessage): string => m.originalMessageId ?? m.id;

/**
 * True when `a` is a later attempt than `b`.
 *
 * Timestamp first. A tie falls back to "a resend is the later event", which holds by definition
 * because a resend is derived from a row that already existed; a full tie orders by id so the
 * surviving row doesn't change between renders.
 */
const isLaterAttempt = (a: InternalMessage, b: InternalMessage): boolean => {
  if (a.createAt.milliseconds !== b.createAt.milliseconds) {
    return a.createAt.milliseconds > b.createAt.milliseconds;
  }
  // Truthy, not `!== null`: the field is optional on the generated type, so an absent pointer has
  // to read as "not a resend" exactly like an explicit null.
  const aIsResend = Boolean(a.originalMessageId);
  const bIsResend = Boolean(b.originalMessageId);
  if (aIsResend !== bIsResend) {
    return aIsResend;
  }
  return a.id > b.id;
};

/** Buckets the rows on screen by the message they are all attempts at. */
const groupAttempts = (messages: readonly InternalMessage[]): Map<string, InternalMessage[]> => {
  const groups = new Map<string, InternalMessage[]>();
  messages.forEach(m => {
    const key = attemptGroupKey(m);
    const group = groups.get(key);
    if (group) {
      group.push(m);
    } else {
      groups.set(key, [m]);
    }
  });
  return groups;
};

/**
 * Numbers every row with which attempt it is: the first send is 1, its first resend is 2, and so on.
 *
 * Derived, never stored — there is no attempt column to drift out of step with the rows.
 *
 * Scoped to the rows on screen, so a number is a floor rather than a lifetime total. Where the first
 * send is missing from the group the numbering starts at 2, because a resend is by definition not
 * the first attempt and calling it attempt 1 would be wrong rather than merely shy. The badge's
 * tooltip says the count comes from these results so the number never reads as authoritative.
 *
 * Single source of truth shared by the messages table and the CSV export so the two can never
 * diverge — the table numbers the one row it keeps, the export numbers every attempt.
 */
export function deriveAttemptNumbers(messages: readonly InternalMessage[]): ReadonlyMap<string, number> {
  const attemptNumberById = new Map<string, number>();
  groupAttempts(messages).forEach(group => {
    const firstSendPresent = group.some(m => !m.originalMessageId);
    [...group].sort((a, b) => (isLaterAttempt(a, b) ? 1 : -1)).forEach((m, index) => attemptNumberById.set(m.id, (firstSendPresent ? 1 : 2) + index));
  });
  return attemptNumberById;
}

/**
 * Collapses each message to its latest attempt — one row per message, however many times it was
 * sent.
 *
 * Without this an original and its resend are two rows that differ only by a badge, which reads as
 * a duplicate message. Nothing is destroyed: the folded-away rows are untouched in the database,
 * and the surviving row expands to show the original it came from.
 *
 * Scoped to the rows on screen, so an attempt whose siblings sit on another page folds nothing.
 * That fails in the safe direction — a row is only ever hidden when the row replacing it is
 * visible. The CSV export deliberately stays unfolded and remains the full per-attempt record.
 *
 * Input order is preserved by filtering rather than rebuilding, so the backend's sort survives.
 */
export function foldToLatestAttempt(messages: readonly InternalMessage[]): InternalMessage[] {
  const survivors = new Set<string>();
  groupAttempts(messages).forEach(group => {
    survivors.add(group.reduce((best, candidate) => (isLaterAttempt(candidate, best) ? candidate : best)).id);
  });

  return messages.filter(m => survivors.has(m.id));
}
